import React, { useState, useEffect } from 'react'
import { Routes, Route, Link, useNavigate, useLocation } from 'react-router-dom'
import { motion } from 'framer-motion'
import { 
  LayoutDashboard, 
  Settings, 
  FileText, 
  Users, 
  Briefcase,
  LogOut,
  Save,
  Plus,
  Edit,
  Trash2,
  Eye,
  BarChart3,
  Globe,
  Shield,
  Target,
  TrendingUp,
  MessageSquare
} from 'lucide-react'

// Admin Components
import ServicesManager from './components/ServicesManager'
import ContentManager from './components/ContentManager'
import CareersManager from './components/CareersManager'
import EnquiriesManager from './components/EnquiriesManager'
import { onAuthStateChanged, signOut } from 'firebase/auth'
import { auth as firebaseAuth } from '@lib/firebase'

const AdminDashboard = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const navigate = useNavigate()
  const location = useLocation()

  // Gate on the real Firebase session, not a localStorage flag anyone can set
  // from the console. The flag is kept only so the redirect does not flash for
  // an already-signed-in admin while onAuthStateChanged resolves.
  useEffect(() => {
    const unsub = onAuthStateChanged(firebaseAuth, (user) => {
      if (user) {
        setIsAuthenticated(true)
        localStorage.setItem('adminAuth', 'true')
      } else {
        setIsAuthenticated(false)
        localStorage.removeItem('adminAuth')
        navigate('/admin/login')
      }
    })
    return unsub
  }, [navigate])

  const handleLogout = async () => {
    localStorage.removeItem('adminAuth')
    try {
      await signOut(firebaseAuth)
    } finally {
      navigate('/admin/login')
    }
  }

  const sidebarItems = [
    { name: 'Dashboard', href: '/admin/dashboard', icon: LayoutDashboard },
    { name: 'Enquiries', href: '/admin/enquiries', icon: MessageSquare },
    { name: 'Services', href: '/admin/services', icon: Briefcase },
    { name: 'Content', href: '/admin/content', icon: FileText },
    { name: 'Careers', href: '/admin/careers', icon: Users },
    { name: 'Settings', href: '/admin/settings', icon: Settings }
  ]

  const isActivePath = (path) => {
    if (path === '/admin/dashboard') {
      return location.pathname === '/admin/dashboard' || location.pathname === '/admin'
    }
    return location.pathname.startsWith(path)
  }

  if (!isAuthenticated) {
    return null
  }

  return (
    <div className="min-h-screen bg-neutral-50 flex">
      {/* Sidebar */}
      <div className="w-64 bg-white shadow-elegant border-r border-neutral-200">
        <div className="p-6 border-b border-neutral-200">
          <div className="flex items-center space-x-3">
            <img 
              src="/TRP_logo_compressed.png" 
              alt="Total Rewards Pro Logo" 
              className="w-12 h-8 object-contain"
            />
            <div>
              <h1 className="text-lg font-open-sans font-semibold text-navy-900">Admin Panel</h1>
              <p className="text-xs text-neutral-600">iTotalRewards Pro LLP</p>
            </div>
          </div>
        </div>

        <nav className="p-4">
          <ul className="space-y-2">
            {sidebarItems.map((item) => (
              <li key={item.name}>
                <Link
                  to={item.href}
                  className={`flex items-center space-x-3 px-4 py-3 rounded-md transition-all duration-200 ${
                    isActivePath(item.href)
                      ? 'bg-primary-50 text-primary-800 border border-primary-200'
                      : 'text-neutral-600 hover:text-primary-800 hover:bg-neutral-50'
                  }`}
                >
                  <item.icon className="w-5 h-5" />
                  <span className="font-medium">{item.name}</span>
                </Link>
              </li>
            ))}
          </ul>
        </nav>

        <div className="absolute bottom-4 left-4 right-4">
          <button
            onClick={handleLogout}
            className="flex items-center space-x-3 w-full px-4 py-3 text-neutral-600 hover:text-red-600 hover:bg-red-50 rounded-md transition-all duration-200"
          >
            <LogOut className="w-5 h-5" />
            <span className="font-medium">Logout</span>
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-auto">
        <Routes>
          <Route path="/" element={<DashboardHome />} />
          <Route path="/dashboard" element={<DashboardHome />} />
          <Route path="/enquiries" element={<EnquiriesManager />} />
          <Route path="/services" element={<ServicesManager />} />
          <Route path="/content" element={<ContentManager />} />
          <Route path="/careers" element={<CareersManager />} />
          <Route path="/settings" element={<SettingsPanel />} />
        </Routes>
      </div>
    </div>
  )
}

// Dashboard Home Component
const DashboardHome = () => {
  const stats = [
            { label: 'Total Services', value: '6', icon: Briefcase, color: 'from-primary-500 to-primary-600' },
        { label: 'New Enquiries', value: '0', icon: MessageSquare, color: 'from-primary-600 to-primary-700' },
        { label: 'Content Pages', value: '6', icon: FileText, color: 'from-primary-400 to-primary-500' },
        { label: 'Career Openings', value: '0', icon: Users, color: 'from-primary-600 to-primary-800' }
  ]

  const recentActivity = [
    { action: 'Updated Services page content', time: '2 hours ago', type: 'edit' },
    { action: 'Added new career opening', time: '1 day ago', type: 'add' },
    { action: 'Modified About page', time: '2 days ago', type: 'edit' },
    { action: 'Published new blog post', time: '3 days ago', type: 'add' }
  ]

  return (
    <div className="p-8">
      <div className="mb-8">
        <h1 className="text-display-3 text-navy-900 mb-2">Dashboard Overview</h1>
        <p className="text-body text-muted">Welcome back! Here's what's happening with your website.</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {stats.map((stat, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: index * 0.1 }}
            className="card p-6"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted mb-1">{stat.label}</p>
                <p className="text-3xl font-bold text-navy-900">{stat.value}</p>
              </div>
              <div className={`w-12 h-12 bg-gradient-to-r ${stat.color} rounded-md flex items-center justify-center`}>
                <stat.icon className="w-6 h-6 text-white" />
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Quick Actions */}
        <div className="card p-6">
          <h3 className="text-heading-2 text-navy-900 mb-6">Quick Actions</h3>
          <div className="space-y-4">
            <Link to="/admin/enquiries" className="flex items-center justify-between p-4 bg-neutral-50 rounded-md hover:bg-neutral-100 transition-colors">
              <div className="flex items-center space-x-3">
                <MessageSquare className="w-5 h-5 text-navy-700" />
                <span className="font-medium text-navy-900">View Enquiries</span>
              </div>
              <Eye className="w-4 h-4 text-neutral-500" />
            </Link>
            <Link to="/admin/services" className="flex items-center justify-between p-4 bg-neutral-50 rounded-md hover:bg-neutral-100 transition-colors">
              <div className="flex items-center space-x-3">
                <Briefcase className="w-5 h-5 text-navy-700" />
                <span className="font-medium text-navy-900">Manage Services</span>
              </div>
              <Edit className="w-4 h-4 text-neutral-500" />
            </Link>
            <Link to="/admin/content" className="flex items-center justify-between p-4 bg-neutral-50 rounded-md hover:bg-neutral-100 transition-colors">
              <div className="flex items-center space-x-3">
                <FileText className="w-5 h-5 text-navy-700" />
                <span className="font-medium text-navy-900">Edit Content</span>
              </div>
              <Edit className="w-4 h-4 text-neutral-500" />
            </Link>
            <Link to="/admin/careers" className="flex items-center justify-between p-4 bg-neutral-50 rounded-md hover:bg-neutral-100 transition-colors">
              <div className="flex items-center space-x-3">
                <Users className="w-5 h-5 text-navy-700" />
                <span className="font-medium text-navy-900">Manage Careers</span>
              </div>
              <Edit className="w-4 h-4 text-neutral-500" />
            </Link>
          </div>
        </div>

        {/* Recent Activity */}
        <div className="card p-6">
          <h3 className="text-heading-2 text-navy-900 mb-6">Recent Activity</h3>
          <div className="space-y-4">
            {recentActivity.map((activity, index) => (
              <div key={index} className="flex items-center space-x-3 p-3 bg-neutral-50 rounded-md">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                                          activity.type === 'edit' ? 'bg-primary-100 text-primary-600' : 'bg-primary-100 text-primary-700'
                }`}>
                  {activity.type === 'edit' ? <Edit className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-navy-900 font-open-sans">{activity.action}</p>
                  <p className="text-xs text-muted">{activity.time}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

// Settings Panel Component
const SettingsPanel = () => {
    const [settings, setSettings] = useState({
    siteName: 'Total Rewards Pro',
    siteDescription: 'People. Purpose. Pay. Perfectly Aligned.',
        contactEmail: 'info@totalrewardspro.com',
    phone: '+91-9873655887',
    maintenanceMode: false,
    analyticsEnabled: true
  })

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target
    setSettings(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }))
  }

  const handleSave = () => {
    // In a real app, this would save to backend
    alert('Settings saved successfully!')
  }

  return (
    <div className="p-8">
      <div className="mb-8">
        <h1 className="text-display-3 text-navy-900 mb-2">Settings</h1>
        <p className="text-body text-muted">Manage your website settings and configuration.</p>
      </div>

      <div className="max-w-2xl">
        <div className="card p-8">
          <h3 className="text-heading-2 text-navy-900 mb-6">General Settings</h3>
          
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-semibold text-navy-900 mb-2">Site Name</label>
              <input
                type="text"
                name="siteName"
                value={settings.siteName}
                onChange={handleChange}
                className="form-input"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-navy-900 mb-2">Site Description</label>
              <input
                type="text"
                name="siteDescription"
                value={settings.siteDescription}
                onChange={handleChange}
                className="form-input"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-navy-900 mb-2">Contact Email</label>
              <input
                type="email"
                name="contactEmail"
                value={settings.contactEmail}
                onChange={handleChange}
                className="form-input"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-navy-900 mb-2">Phone Number</label>
              <input
                type="text"
                name="phone"
                value={settings.phone}
                onChange={handleChange}
                className="form-input"
              />
            </div>

            <div className="flex items-center space-x-3">
              <input
                type="checkbox"
                name="maintenanceMode"
                checked={settings.maintenanceMode}
                onChange={handleChange}
                className="w-4 h-4 text-navy-600 border-neutral-300 rounded focus:ring-navy-500"
              />
              <label className="text-sm font-medium text-navy-900 font-open-sans">Enable Maintenance Mode</label>
            </div>

            <div className="flex items-center space-x-3">
              <input
                type="checkbox"
                name="analyticsEnabled"
                checked={settings.analyticsEnabled}
                onChange={handleChange}
                className="w-4 h-4 text-navy-600 border-neutral-300 rounded focus:ring-navy-500"
              />
              <label className="text-sm font-medium text-navy-900 font-open-sans">Enable Analytics</label>
            </div>

            <button onClick={handleSave} className="btn-primary">
              <Save className="w-4 h-4 mr-2" />
              Save Settings
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default AdminDashboard 